import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from "react-router-dom";
import LogoHpa from './images/logo.png';
import debounce from './debounce';
import { genererHTMLReçu } from './generateReceiptHTML';
import { genererPDF } from './generateReceiptPDF';
import { loginUser } from '../utils/auth';

const options = [
  { name: "En espèce", value: "espece" },
  { name: "Par Chèque", value: "cheque" },
  { name: "Par Virement", value: "virement" },
  { name: "Transfert AirTel", value: "transfert_airtel" },
  { name: "Transfert TIGO", value: "transfer_tigo" },
  { name: "Orange Money", value: "orange_money" },
  { name: "Moov Money", value: "moov_money" }
];

// Services d'analyses pour GesLAB
const services = [
  { name: "Choisir une analyse", value: "", icon: "📋", modal: null },
  { name: "Analyses Physico-chimiques", value: "physicochimie", icon: "🧪", modal: "analyses" },
  { name: "Analyses Bactériologiques", value: "bacteriologie", icon: "🦠", modal: "analyses" }
];

const assureur = [
  { name: "Non", value: "Non" },
  { name: "ASCOMA", value: "ASCOMA" },
  { name: "STAR International", value: "STAR International" },
  { name: "CNPS", value: "CNPS" },
  { name: "MUTUELLE", value: "MUTUELLE" }
];

const errors = {
  uname: "Utilisateur Inconnu",
  pass: "Erreur sur Code d'accès"
};

// Actes médicaux par catégorie
const actesParCategorie = {
  "Consultations": [
    { id: 1, name: "Consultation générale", montant: 5000 },
    { id: 2, name: "Consultation spécialisée", montant: 10000 },
    { id: 3, name: "Consultation de suivi", montant: 3000 }
  ],
  "Gynécologie": [
    { id: 17, name: "Consultation gynécologique", montant: 5000 },
    { id: 18, name: "Échographie obstétricale", montant: 35000 },
    { id: 19, name: "Accouchement normal", montant: 300000 },
    { id: 20, name: "Césarienne", montant: 800000 }
  ],
  "Urgences": [
    { id: 26, name: "Consultation urgente", montant: 8000 },
    { id: 27, name: "Réanimation", montant: 50000 },
    { id: 28, name: "Soins intensifs (jour)", montant: 100000 }
  ],
  "Hospitalisation": [
    { id: 31, name: "Chambre simple (jour)", montant: 25000 },
    { id: 32, name: "Chambre double (jour)", montant: 15000 },
    { id: 33, name: "Suite royale (jour)", montant: 50000 }
  ],
  "Chirurgie": [
    { id: 13, name: "Hernie inguinale", montant: 600000 },
    { id: 14, name: "Appendicectomie", montant: 700000 },
    { id: 15, name: "Lipome", montant: 200000 }
  ],
  "Kinésithérapie": [
    { id: 35, name: "Séance de kiné", montant: 10000 },
    { id: 36, name: "Rééducation fonctionnelle", montant: 15000 }
  ],
  "Fibroscopie": [
    { id: 38, name: "Fibroscopie gastrique", montant: 50000 },
    { id: 39, name: "Coloscopie", montant: 75000 }
  ]
};

// Modal de sélection des services
const ServiceModal = ({ isOpen, onClose, onValidate, serviceType, socket }) => {
  const [selectedItems, setSelectedItems] = useState([]);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  // Charger les données selon le type de service
  useEffect(() => {
    if (isOpen && serviceType) {
      setSelectedItems([]);
      setSearchTerm('');
      setLoading(true);

      if (serviceType === 'analyses' && socket) {
        socket.emit('load_analyses', (response) => {
          setLoading(false);
          if (response && response.success) {
            const analysesData = response.data?.analyses || [];
            setItems(analysesData.map(a => ({ ...a, nom: a.nom, prix: a.prix, type: a.type })));
          } else {
            console.error('Erreur chargement analyses');
            setItems([]);
          }
        });
      } 
      else if (serviceType === 'medicaments' && socket) {
        socket.emit('load_medicaments', (response) => {
          setLoading(false);
          if (response && response.success) {
            setItems(response.data || []);
          } else {
            console.error('Erreur chargement médicaments');
            setItems([]);
          }
        });
      }
      else if (serviceType === 'examens' && socket) {
        socket.emit('load_types_examens', (response) => {
          setLoading(false);
          if (response && response.success) {
            setItems(response.data || []);
          } else {
            console.error('Erreur chargement examens');
            setItems([]);
          }
        });
      }
      else if (serviceType === 'actes') {
        setTimeout(() => {
          setLoading(false);
          const allActes = [];
          Object.keys(actesParCategorie).forEach(cat => {
            actesParCategorie[cat].forEach(acte => {
              allActes.push({ ...acte, categorie: cat, prix: acte.montant, nom: acte.name });
            });
          });
          setItems(allActes);
        }, 100);
      }
      else {
        setLoading(false);
        setItems([]);
      }
    }
  }, [isOpen, serviceType, socket]);

  // Grouper les items par catégorie
  const groupedItems = items.reduce((acc, item) => {
    const category = item.categorie || item.type || "Autres";
    if (!acc[category]) acc[category] = [];
    acc[category].push(item);
    return acc;
  }, {});

  // Filtrer par recherche
  const filterItems = (item) => {
    if (!searchTerm) return true;
    const nom = (item.nom || item.libelle || item.name || '').toLowerCase();
    return nom.includes(searchTerm.toLowerCase());
  };

  const handleSelectItem = (item) => {
    setSelectedItems(prev => {
      const existing = prev.find(i => i.id === item.id);
      const prix = item.prix || item.montant || 0;
      const nom = item.nom || item.libelle || item.name || 'Item';
      
      if (existing) {
        return prev.map(i => 
          i.id === item.id 
            ? { 
                ...i, 
                quantite: (i.quantite || 1) + 1, 
                total: ((i.quantite || 1) + 1) * prix 
              }
            : i
        );
      }
      return [...prev, { 
        ...item, 
        id: item.id,
        quantite: 1, 
        total: prix,
        nom: nom,
        prix: prix
      }];
    });
  };

  const removeItem = (id) => {
    setSelectedItems(prev => prev.filter(i => i.id !== id));
  };

  const updateQuantity = (id, delta) => {
    setSelectedItems(prev => prev.map(i => {
      if (i.id === id) {
        const newQty = Math.max(1, (i.quantite || 1) + delta);
        return { ...i, quantite: newQty, total: newQty * (i.prix || i.montant || 0) };
      }
      return i;
    }));
  };

  const getTotal = () => {
    return selectedItems.reduce((sum, i) => sum + (i.total || 0), 0);
  };

  const handleValidate = () => {
    onValidate({
      items: selectedItems,
      totalPrice: getTotal(),
      count: selectedItems.length,
      serviceType: serviceType
    });
  };

  if (!isOpen) return null;

  const getServiceTitle = () => {
    const titles = {
      analyses: "Sélection des Analyses de Laboratoire",
      medicaments: "Sélection des Médicaments",
      examens: "Sélection des Examens d'Imagerie",
      actes: "Sélection des Actes Médicaux"
    };
    return titles[serviceType] || "Sélection des Services";
  };

  return (
    <div className="modal-overlay">
      <div className="confirmation-modal" style={{ width: '90%', maxWidth: '1000px' }}>
        <div className="modal-header">
          <h3>{getServiceTitle()}</h3>
          <button className="btn-fermer-modal" onClick={onClose} style={{ background: 'none', border: 'none', color: 'white', fontSize: '24px', cursor: 'pointer' }}>✕</button>
        </div>

        <div className="modal-content" style={{ padding: '20px', maxHeight: '70vh', overflow: 'auto' }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: '40px' }}>Chargement...</div>
          ) : (
            <div style={{ display: 'flex', gap: '20px', flexWrap: 'wrap' }}>
              <div style={{ flex: 2 }}>
                <div style={{ marginBottom: '15px' }}>
                  <input
                    type="text"
                    placeholder="Rechercher..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    style={{ width: '100%', padding: '10px', border: '1px solid #ddd', borderRadius: '8px' }}
                  />
                </div>
                
                {Object.keys(groupedItems).length === 0 ? (
                  <div style={{ textAlign: 'center', padding: '40px', color: '#999' }}>
                    Aucun élément disponible
                  </div>
                ) : (
                  Object.entries(groupedItems).map(([category, categoryItems]) => {
                    const filtered = categoryItems.filter(filterItems);
                    if (filtered.length === 0) return null;
                    return (
                      <div key={category} style={{ marginBottom: '20px' }}>
                        <h4 style={{ color: '#2E7D32', marginBottom: '10px', borderBottom: '1px solid #ddd', paddingBottom: '5px' }}>
                          {category}
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(250px, 1fr))', gap: '10px' }}>
                          {filtered.map(item => (
                            <div key={item.id} style={{ border: '1px solid #eee', borderRadius: '8px', padding: '10px', background: '#f9f9f9' }}>
                              <div style={{ fontWeight: 'bold' }}>{item.nom || item.libelle || item.name}</div>
                              <div style={{ color: '#28a745', fontWeight: 'bold' }}>
                                {(item.prix || item.montant || 0).toLocaleString()} FCFA
                              </div>
                              {item.stock !== undefined && (
                                <div style={{ fontSize: '11px', color: item.stock < 10 ? '#dc3545' : '#666' }}>
                                  Stock: {item.stock}
                                </div>
                              )}
                              <button
                                onClick={() => handleSelectItem(item)}
                                style={{ marginTop: '8px', width: '100%', padding: '5px', background: '#28a745', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}
                              >
                                Ajouter
                              </button>
                            </div>
                          ))}
                        </div>
                      </div>
                    );
                  })
                )}
              </div>

              <div style={{ flex: 1, background: '#f5f5f5', borderRadius: '10px', padding: '15px', position: 'sticky', top: '20px', height: 'fit-content' }}>
                <h4>Panier ({selectedItems.length})</h4>
                {selectedItems.length === 0 ? (
                  <p style={{ color: '#999', textAlign: 'center', padding: '20px' }}>Aucun élément sélectionné</p>
                ) : (
                  <>
                    {selectedItems.map(item => (
                      <div key={item.id} style={{ borderBottom: '1px solid #ddd', padding: '8px 0' }}>
                        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: '5px' }}>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontSize: '13px', fontWeight: 'bold' }}>{item.nom}</div>
                            <div style={{ fontSize: '11px', color: '#666' }}>{item.categorie || item.type}</div>
                          </div>
                          <div style={{ display: 'flex', alignItems: 'center', gap: '5px' }}>
                            <button onClick={() => updateQuantity(item.id, -1)} style={{ width: '25px', height: '25px', borderRadius: '50%', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>-</button>
                            <span style={{ width: '30px', textAlign: 'center' }}>{item.quantite || 1}</span>
                            <button onClick={() => updateQuantity(item.id, 1)} style={{ width: '25px', height: '25px', borderRadius: '50%', border: '1px solid #ddd', background: 'white', cursor: 'pointer' }}>+</button>
                          </div>
                          <div style={{ minWidth: '80px', textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>
                            {(item.total || 0).toLocaleString()} FCFA
                          </div>
                          <button onClick={() => removeItem(item.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '16px' }}>✕</button>
                        </div>
                      </div>
                    ))}
                    <div style={{ marginTop: '15px', paddingTop: '10px', borderTop: '2px solid #ddd', display: 'flex', justifyContent: 'space-between', fontWeight: 'bold', fontSize: '16px' }}>
                      <span>Total:</span>
                      <span style={{ color: '#28a745' }}>{getTotal().toLocaleString()} FCFA</span>
                    </div>
                  </>
                )}
                <button
                  onClick={handleValidate}
                  disabled={selectedItems.length === 0}
                  style={{ marginTop: '15px', width: '100%', padding: '12px', background: selectedItems.length === 0 ? '#ccc' : 'linear-gradient(135deg, #2E7D32, #1e3a8a)', color: 'white', border: 'none', borderRadius: '8px', fontWeight: 'bold', cursor: selectedItems.length === 0 ? 'not-allowed' : 'pointer' }}
                >
                  Valider ({selectedItems.length} éléments)
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

const MG_Caisse = ({ socket }) => {
  const navigate = useNavigate();
  const [errorMessages, setErrorMessages] = useState({});
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [loading, setLoading] = useState(false);
  const [searchResults, setSearchResults] = useState(null);
  const [socketConnected, setSocketConnected] = useState(false);
  const [showConfirmation, setShowConfirmation] = useState(false);
  const [ClientEnregistre, setClientEnregistre] = useState(null);
  const [tousLesClients, setTousLesÉchantillons] = useState([]);
  const [showChargerModal, setShowChargerModal] = useState(false);
  const [csrCharger, setCsrCharger] = useState('');
  const [chargerLoading, setChargerLoading] = useState(false);
  const [showImprimerModal, setShowImprimerModal] = useState(false);
  const [csrImprimer, setCsrImprimer] = useState('');
  const [ClientAImprimer, setPatientAImprimer] = useState(null);

  // États pour la sélection des services
  const [activeModal, setActiveModal] = useState(null);
  const [selectedItems, setSelectedItems] = useState([]);
  const [selectedService, setSelectedService] = useState('');
  const [selectedServiceData, setSelectedServiceData] = useState(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  // État des données du formulaire
   const [formData, setFormData] = useState({
    numClient: '',
    nomClient: '',
    numID_LNE: '',
    numAirTel: '',
    numTIGO: "",
    demandeur: "",
    mode_Paie_OP: "",
    service: "",
    assure: "Non",
    total_OP: "",
    remise_OP: "",
    dette_OP: "",
    jeton_OP: generateJeton(),
    isLaboratorized: "En attente",
    typeEau: "",
    pointPrelevement: "",
    datePrelevement: new Date().toISOString().slice(0, 16),
    nbEchantillons: 1
  });

  // Fonction pour générer un jeton
  function generateJeton() {
    return Math.random().toString(36).substring(2, 15) + Math.random().toString(36).substring(2, 15);
  }

  // Vérifier la connexion socket
  useEffect(() => {
    if (socket) {
      const handleConnect = () => {
        console.log('✅ Caisse - Socket connecté');
        setSocketConnected(true);
        socket.emit('module_connected', { module: 'Caisse', socketId: socket.id });
        chargerTousLesÉchantillons();
      };
      const handleDisconnect = () => {
        console.log('❌ Caisse - Socket déconnecté');
        setSocketConnected(false);
      };
      socket.on('connect', handleConnect);
      socket.on('disconnect', handleDisconnect);
      if (socket.connected) {
        setSocketConnected(true);
        socket.emit('module_connected', { module: 'Caisse', socketId: socket.id });
        chargerTousLesÉchantillons();
      }
      return () => {
        socket.off('connect', handleConnect);
        socket.off('disconnect', handleDisconnect);
      };
    }
  }, [socket]);

  // Charger tous les patients
  const chargerTousLesÉchantillons = useCallback(() => {
    if (!socket || !socket.connected) return;
    socket.emit('get_all_clients', (response) => {
      if (response && response.success) {
        setTousLesÉchantillons(response.Clients || []);
      }
    });
  }, [socket, socketConnected]);

  // Validation de la sélection du modal
  const handleValidateModal = (data) => {
    console.log('✅ Validation modal:', data);
    
    const normalizedItems = data.items.map(item => ({
      id: item.id,
      nom: item.nom || item.libelle || item.name,
      prix: item.prix || item.montant || 0,
      quantite: item.quantite || 1,
      total: item.total || (item.prix || item.montant || 0) * (item.quantite || 1),
      type: item.type || item.categorie
    }));
    
    setSelectedItems(normalizedItems);
    setSelectedServiceData({
      ...data,
      items: normalizedItems
    });
    setFormData(prev => ({
      ...prev,
      total_OP: data.totalPrice
    }));
    setActiveModal(null);
  };

  // Gestion du changement de service
  const handleServiceChange = (e) => {
    const serviceValue = e.target.value;
    console.log('🎯 Service sélectionné:', serviceValue);
    
    setSelectedItems([]);
    setSelectedServiceData(null);
    setFormData(prev => ({ ...prev, service: serviceValue, total_OP: "" }));
    setSelectedService(serviceValue);

    if (!serviceValue) {
      setActiveModal(null);
      return;
    }

    const serviceConfig = services.find(s => s.value === serviceValue);
    if (!serviceConfig || !serviceConfig.modal) {
      console.log('⚠️ Pas de modal pour ce service');
      return;
    }

    console.log('🔓 Ouverture du modal:', serviceConfig.modal);
    
    setTimeout(() => {
      setActiveModal(serviceConfig.modal);
    }, 100);
  };

  // Fermer le modal
  const handleCloseModal = () => {
    if (selectedItems.length === 0 && selectedService) {
      setFormData(prev => ({ ...prev, service: "" }));
      setSelectedService("");
    }
    setActiveModal(null);
  };

  // Supprimer un élément sélectionné
  const removeSelectedItem = (itemId) => {
    const newItems = selectedItems.filter(i => i.id !== itemId);
    setSelectedItems(newItems);
    const newTotal = newItems.reduce((sum, i) => sum + (i.total || i.prix || 0), 0);
    setFormData(prev => ({ ...prev, total_OP: newTotal }));
  };

  // Calculer le montant final après remise et dette
  const calculerMontantFinal = () => {
    const totalBrut = parseFloat(formData.total_OP) || 0;
    const remise = parseFloat(formData.remise_OP) || 0;
    const dette = parseFloat(formData.dette_OP) || 0;
    const montantApresRemise = totalBrut - (totalBrut * remise / 100);
    return Math.max(0, montantApresRemise - dette);
  };

  // Recherche de patient
  const searchClient = useCallback(
    debounce((csrNumber) => {
      if (!csrNumber || csrNumber.length < 3) {
        setLoading(false);
        setSearchResults(null);
        return;
      }
      setLoading(true);
      const ClientLocal = tousLesClients.find(p => p.numID_LNE === csrNumber || p.numClient === csrNumber);
      if (ClientLocal) {
        setLoading(false);
        setSearchResults(ClientLocal);
        setFormData(prev => ({
          ...prev,
          nomClient: ClientLocal.nomClient || '',
          numAirTel: ClientLocal.numAirTel || '',
          numTIGO: ClientLocal.numTIGO || '',
        }));
        return;
      }
      if (!socket || !socket.connected) {
        setLoading(false);
        setSearchResults(null);
        return;
      }
      socket.emit('get_Client_by_csr', csrNumber, (response) => {
        setLoading(false);
        if (response && response.success && response.Client) {
          setSearchResults(response.Client);
          setFormData(prev => ({
            ...prev,
            nomClient: response.Client.nomClient || '',
            numAirTel: response.Client.numAirTel || '',
            numTIGO: response.Client.numTIGO || '',
          }));
        } else {
          setSearchResults(null);
        }
      });
    }, 500),
    [socket, tousLesClients]
  );

  const handleLNEChange = (e) => {
    const value = e.target.value;
    setFormData(prev => ({ ...prev, numID_LNE: value }));
    searchClient(value);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: value });
  };

  const clearForm = () => {
    setFormData({
      numClient: '',
      nomClient: '',
      numID_LNE: '',
      numAirTel: '',
      numTIGO: "",
      demandeur: "",
      mode_Paie_OP: "",
      service: "",
      assure: "Non",
      total_OP: "",
      remise_OP: "",
      dette_OP: "",
      jeton_OP: generateJeton(),
      isLaboratorized: "En attente",
      typeEau: "",
      pointPrelevement: "",
      datePrelevement: new Date().toISOString().slice(0, 16),
      nbEchantillons: 1
    });
    setSelectedItems([]);
    setSelectedServiceData(null);
    setSelectedService("");
    setSearchResults(null);
    setActiveModal(null);
  };

  // Soumission du formulaire
// Remplacez la fonction handleSubmit par celle-ci :
const handleSubmit = (e) => {
  e.preventDefault();
  setIsSubmitting(true);
  
  console.log('🔍 ========== DÉBUT HANDLESUBMIT ==========');
  console.log('1. Vérification des champs obligatoires...');
  console.log('   - nomClient:', formData.nomClient);
  console.log('   - service:', formData.service);
  console.log('   - selectedItems.length:', selectedItems.length);
  
  
  if (!formData.nomClient) {
    console.log('❌ Erreur: Nom du patient manquant');
    alert("Le nom du Laboratoire / Client est obligatoire");
    setIsSubmitting(false);
    return;
  }
  if (!formData.service) {
    console.log('❌ Erreur: Service non sélectionné');
    alert("Veuillez sélectionner un service");
    setIsSubmitting(false);
    return;
  }
  if (selectedItems.length === 0) {
    console.log('❌ Erreur: Aucun élément sélectionné');
    alert("Veuillez sélectionner au moins un élément pour ce service");
    setIsSubmitting(false);
    return;
  }
  
  console.log('✅ Validation OK');
  console.log('2. Calcul du montant final...');
  
  const montantFinal = calculerMontantFinal();
  console.log('   Montant final:', montantFinal);
  
  console.log('3. Construction des données...');
  const submissionData = {
    ...formData,
    total_OP: montantFinal,
    montant: montantFinal,
    items: selectedItems,
    serviceDetails: selectedServiceData,
    dateCreation: new Date().toISOString()
  };
  
  console.log('📤 Données à envoyer:', {
    nomClient: submissionData.nomClient,
    service: submissionData.service,
    itemsCount: submissionData.items?.length,
    montant: submissionData.montant,
    socketConnected: socketConnected,
    socketId: socket?.id
  });
  
  console.log('4. Envoi au serveur...');
  
  // Vérifier que le socket est connecté
  if (!socket) {
    console.error('❌ Socket non défini!');
    alert("Erreur: Socket non défini");
    setIsSubmitting(false);
    return;
  }
  
  if (!socketConnected) {
    console.error('❌ Socket non connecté!');
    alert("Erreur de connexion au serveur. Veuillez vérifier que le serveur est démarré.");
    setIsSubmitting(false);
    return;
  }
  
  // Tester si l'émission fonctionne
  console.log('📡 Émission de l\'événement "enregistrer_Client"...');
  
  socket.emit("enregistrer_Client", submissionData, (response) => {
    console.log('📩 Réponse reçue du serveur:', response);
    
    if (response && response.success) {
      console.log('✅ Enregistrement réussi!');
      
      setClientEnregistre(ClientAvecDonnees);
      setShowConfirmation(true);
      chargerTousLesClients();
      
      clearForm();
      console.log('========== FIN HANDLESUBMIT (SUCCÈS) ==========');
    } else {
      const errorMsg = response?.message || "Erreur inconnue lors de l'enregistrement";
      console.error('❌ Erreur enregistrement:', errorMsg);
      console.error('   Réponse complète:', response);
      alert("Erreur lors de l'enregistrement: " + errorMsg);
      console.log('========== FIN HANDLESUBMIT (ÉCHEC) ==========');
    }
    setIsSubmitting(false);
  });
};
  const imprimerReçu = () => {
    if (!ClientEnregistre) return;
    const printWindow = window.open('', '_blank');
    printWindow.document.write(genererHTMLReçu(ClientEnregistre, formData.mode_Paie_OP));
    printWindow.document.close();
  };

  const fermerSansImprimer = () => {
    setShowConfirmation(false);
    setClientEnregistre(null);
  };

  const handleLogin = async (event) => {
    event.preventDefault();
    const uname = event.target.uname.value;
    const pass = event.target.pass.value;
    const result = await loginUser(uname, pass);
    if (result.success) {
      setIsSubmitted(true);
    } else {
      setErrorMessages({ name: "pass", message: result.message || errors.pass });
    }
  };

  const renderErrorMessage = (name) =>
    name === errorMessages.name && (
      <div className="message_erreur">{errorMessages.message}</div>
    );

  const renderForm = (
    <div className="form">
      <form className='form_margin BC' onSubmit={handleLogin}>
        <br />
        <br />
        <label className='sous_titre'>Utilisateur </label>
        <input className='utilisateur_interne' minLength={4} type="text" name="uname" required />
        {renderErrorMessage("uname")}
        <label className='sous_titre'>Code d'accès</label>
        <input className='utilisateur_interne' minLength={4} type="password" name="pass" required />
        {renderErrorMessage("pass")}
        <div className='ftt__footer BC'>
          <button className='home__cta'>Connexion</button>
        </div>
      </form>
    </div>
  );

  const sirAcceuil = () => navigate("/PageAcceuil");
  const sirApercu = () => navigate("/MgJournaux");
  const sirCharger = () => setShowChargerModal(true);

  if (!isSubmitted) return renderForm;

  const selectedServiceInfo = services.find(s => s.value === formData.service);

  return (
    <>
      <div className="entete TC marges_logo_5px">
        <div className="marges_logo_5px">
          <img className='logo_clinique marges_logo_5px' src={LogoHpa} alt="LNE" id="logo" />
        </div>
        <div className='titre_entete'>
          <div className="titre-container">
            <h2 className='titre_entete'>LNE - Ndjamena</h2>
          </div>
          <h3 className='sous_titre_entete'>Réception des Échantillons</h3>
        </div>
      </div>

      <div className="socket-info">
        <p>
          Serveur {socketConnected ? '✅ Connecté' : '❌ Déconnecté'} |
          {tousLesClients.length} Échantillons chargés
          <button onClick={chargerTousLesÉchantillons} style={{ marginLeft: '10px', padding: '2px 8px', fontSize: '12px' }} title="Rafraîchir">🔄</button>
        </p>
      </div>

      {/* Affichage des éléments sélectionnés */}
      {selectedItems.length > 0 && selectedServiceInfo && (
        <div className="selections-section" style={{ margin: '15px 20px', background: 'white', borderRadius: '10px', padding: '15px', boxShadow: '0 2px 5px rgba(0,0,0,0.1)' }}>
          <h4 style={{ marginBottom: '10px', color: '#2E7D32' }}>
            {selectedServiceInfo.icon} {selectedServiceInfo.name} ({selectedItems.length} élément(s))
          </h4>
          <div style={{ maxHeight: '200px', overflow: 'auto' }}>
            {selectedItems.map(item => (
              <div key={item.id} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '8px 0', borderBottom: '1px solid #eee', flexWrap: 'wrap', gap: '5px' }}>
                <span style={{ flex: 2 }}>{item.nom}</span>
                <span style={{ minWidth: '80px', textAlign: 'center' }}>{item.quantite || 1} x {(item.prix || 0).toLocaleString()} FCFA</span>
                <span style={{ minWidth: '100px', textAlign: 'right', fontWeight: 'bold', color: '#28a745' }}>{(item.total || item.prix || 0).toLocaleString()} FCFA</span>
                <button onClick={() => removeSelectedItem(item.id)} style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', fontSize: '18px' }}>✕</button>
              </div>
            ))}
          </div>
          <div style={{ marginTop: '10px', textAlign: 'right', fontWeight: 'bold' }}>
            Total: {selectedItems.reduce((sum, i) => sum + (i.total || i.prix || 0), 0).toLocaleString()} FCFA
          </div>
        </div>
      )}

      {/* Formulaire principal */}
      <form className='form_margin' onSubmit={handleSubmit}>
        <div className='flex-row' style={{ gap: '5px', marginBottom: '5px' }}>
          <div className='top_titre separ_horizontal_5px'>
            <input
              className='usertel__input color_vert_champ'
              value={formData.nomClient}
              onChange={handleChange}
              type="text"
              name="nomClient"
              placeholder="Nom du Laboratoire / Client *"
              required
            />
          </div>
          <div className='top_titre separ_horizontal_5px'>
            <input
              className='usertel__input color_vert_champ'
              value={formData.numAirTel}
              onChange={handleChange}
              type="tel"
              name="numAirTel"
              placeholder="Numéro Téléphone"
            />
          </div>
        </div>

        <div className='flex-row' style={{ gap: '5px', marginBottom: '5px' }}>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_vert_champ'
              value={formData.numID_LNE}
              onChange={handleLNEChange}
              type="text"
              name="numID_LNE"
              placeholder="Numéro LNE (pour échantillon existant)"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_vert_champ'
              value={formData.demandeur}
              onChange={handleChange}
              type="text"
              name="demandeur"
              placeholder="Demandeur"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <select
              className="mode_paie color_vert_list"
              onChange={handleServiceChange}
              value={formData.service}
              name="service"
              required
            >
              {services.map((opt, key) => (
                <option key={key} value={opt.value}>
                  {opt.icon} {opt.name}
                </option>
              ))}
            </select>
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <select
              className="mode_paie color_vert_list"
              onChange={handleChange}
              value={formData.assure}
              name="assure"
            >
              {assureur.map((opt, key) => (
                <option key={key} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='flex-row' style={{ gap: '5px', marginBottom: '5px' }}>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <select
              className="mode_paie color_vert_list"
              onChange={handleChange}
              value={formData.typeEau}
              name="typeEau"
            >
              <option value="">Type d'eau</option>
              <option value="Eau potable">Eau potable</option>
              <option value="Eau de surface">Eau de surface</option>
              <option value="Eau souterraine">Eau souterraine</option>
              <option value="Eau usée">Eau usée</option>
              <option value="Eau de piscine">Eau de piscine</option>
              <option value="Eau minérale">Eau minérale</option>
            </select>
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_vert_champ'
              value={formData.pointPrelevement}
              onChange={handleChange}
              type="text"
              name="pointPrelevement"
              placeholder="Point de prélèvement / Source"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_vert_champ'
              value={formData.datePrelevement}
              onChange={handleChange}
              type="datetime-local"
              name="datePrelevement"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_vert_champ'
              value={formData.nbEchantillons}
              onChange={handleChange}
              type="number"
              name="nbEchantillons"
              placeholder="Nb échantillons"
              min="1"
            />
          </div>
        </div>

        <div className='flex-row' style={{ gap: '5px', marginBottom: '5px' }}>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='userTotal__input largeur_etiq_caisse color_blue_drapeau'
              value={formData.total_OP}
              onChange={handleChange}
              type="number"
              name="total_OP"
              placeholder="Montant total"
              readOnly
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_jaune_drapeau'
              value={formData.remise_OP}
              onChange={handleChange}
              type="number"
              name="remise_OP"
              placeholder="Remise (%)"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <input
              className='usertel__input largeur_etiq_caisse color_rouge_drapeau'
              value={formData.dette_OP}
              onChange={handleChange}
              type="number"
              name="dette_OP"
              placeholder="Dette"
            />
          </div>
          <div className='largeur_div_tel separ_horizontal_5px top_titre'>
            <select
              className="mode_paie color_vert_list"
              onChange={handleChange}
              value={formData.mode_Paie_OP}
              name="mode_Paie_OP"
            >
              {options.map((opt, key) => (
                <option key={key} value={opt.value}>
                  {opt.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className='ftt__footer BC sep' style={{ marginTop: '25px', justifyContent: 'center' }}>
<button 
  className="glow-on-hover MenuBtn" 
  type="submit" 
  disabled={isSubmitting}
  style={{ opacity: isSubmitting ? 0.6 : 1 }}
>
  {isSubmitting ? 'Enregistrement...' : 'Enregistrer'}
</button>
          <button className="glow-on-hover MenuBtn" type="button" onClick={sirApercu}>Journaux</button>
          <button className="glow-on-hover MenuBtn" type="button" onClick={sirCharger}>Charger Patient</button>
          <button className="glow-on-hover MenuBtn" type="button" onClick={() => setShowImprimerModal(true)}>Imprimer Fiche</button>
          <button className="glow-on-hover MenuBtn" type="button" onClick={sirAcceuil}>Fermer</button>
        </div>
      </form>

      {/* Modal de sélection des services */}
      <ServiceModal
        isOpen={activeModal !== null}
        onClose={handleCloseModal}
        onValidate={handleValidateModal}
        serviceType={activeModal}
        socket={socket}
      />

      {/* Modal de confirmation */}
      {showConfirmation && ClientEnregistre && (
        <div className="modal-overlay">
          <div className="confirmation-modal pdf-modal">
            <div className="modal-header">
              <h3>✅ Enregistrement Réussi - Numéro LNE: {ClientEnregistre.numID_LNE}</h3>
            </div>
            <div className="modal-content-pdf">
              <div className="modal-actions">
                <button className="btn-imprimer" onClick={imprimerReçu}>🖨️ Imprimer le reçu</button>
                <button className="btn-fermer" onClick={fermerSansImprimer}>❌ Fermer</button>
              </div>
              <div className="pdf-preview-container">
                {genererPDF(null, null, formData, ClientEnregistre)}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour imprimer un reçu existant */}
      {showImprimerModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>🖨️ Impression de Reçu</h3>
            </div>
            <div className="modal-content-pdf">
              <div className="recherche-section">
                <div className="recherche-form">
                  <label>Numéro LNE:</label>
                  <div className="recherche-input-group">
                    <input
                      type="text"
                      placeholder="Ex: LNE-ID00000001"
                      value={csrImprimer}
                      onChange={(e) => setCsrImprimer(e.target.value)}
                      className="recherche-input"
                    />
                    <button className="btn-rechercher" onClick={() => {
                      if (!csrImprimer) return;
                      socket.emit('get_Client_by_csr', csrImprimer, (response) => {
                        if (response && response.success && response.Client) {
                          setPatientAImprimer(response.Client);
                        } else {
                          alert("Échantillon non trouvé");
                        }
                      });
                    }}>Rechercher</button>
                  </div>
                </div>
                {ClientAImprimer && (
                  <div className="patient-trouve">
                    <h4>Échantillon: {ClientAImprimer.nomClient}</h4>
                    <p>Montant: {ClientAImprimer.montant?.toLocaleString()} FCFA</p>
                    <button className="btn-imprimer" onClick={() => {
                      const printWindow = window.open('', '_blank');
                      printWindow.document.write(genererHTMLReçu(ClientAImprimer, ClientAImprimer.mode_Paie_OP));
                      printWindow.document.close();
                    }}>🖨️ Imprimer</button>
                  </div>
                )}
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-fermer" onClick={() => {
                setShowImprimerModal(false);
                setPatientAImprimer(null);
                setCsrImprimer('');
              }}>Fermer</button>
            </div>
          </div>
        </div>
      )}

      {/* Modal pour charger un patient existant */}
      {showChargerModal && (
        <div className="modal-overlay">
          <div className="confirmation-modal">
            <div className="modal-header">
              <h3>📥 Charger un Échantillon Existant</h3>
            </div>
            <div className="modal-content-pdf">
              <div className="recherche-section">
                <div className="recherche-form">
                  <label>Numéro LNE:</label>
                  <div className="recherche-input-group">
                    <input
                      type="text"
                      placeholder="Ex: LNE-ID00000001"
                      value={csrCharger}
                      onChange={(e) => setCsrCharger(e.target.value)}
                      className="recherche-input"
                    />
                    <button className="btn-rechercher" onClick={() => {
                      if (!csrCharger) return;
                      setChargerLoading(true);
                      socket.emit('get_Client_by_csr', csrCharger, (response) => {
                        setChargerLoading(false);
                        if (response && response.success && response.Client) {
                          const patient = response.Client;
                          setFormData(prev => ({
                            ...prev,
                            nomClient: Client.nomClient || '',
                            numAirTel: Client.numAirTel || '',
                            numID_LNE: Client.numID_LNE || '',
                            demandeur: Client.demandeur || '',
                            assure: Client.assure || 'Non'
                          }));
                          if (Client.items) setSelectedItems(Client.items);
                          if (Client.service) {
                            setFormData(prev => ({ ...prev, service: Client.service }));
                            setSelectedService(Client.service);
                          }
                          setShowChargerModal(false);
                          alert(`✅ Patient ${Client.nomClient} chargé avec succès`);
                        } else {
                          alert("Échantillon non trouvé");
                        }
                      });
                    }} disabled={chargerLoading}>
                    {chargerLoading ? 'Recherche...' : 'Charger'}
                  </button>
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-actions">
              <button className="btn-fermer" onClick={() => {
                setShowChargerModal(false);
                setCsrCharger('');
              }}>Fermer</button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default MG_Caisse;
